import { useState, useImperativeHandle, forwardRef } from 'react';
import type { InvestigationResult } from '../../api/osint';

interface Props {
  result: InvestigationResult;
  investigationType: string;
}

export type TabId = 'overview' | 'timeline' | 'entities' | 'domain' | 'network' | 'email' | 'social' | 'crypto' | 'darkweb' | 'person';

export interface InvestigationRealResultsRef {
  switchTab: (tabId: TabId) => void;
}

// Source count reference
const ALL_SEARCH_ENGINES_COUNT = { duckduckgo: 1, bing: 1, brave: 1, google_news: 1, yandex: 1, startpage: 1, qwant: 1, mojeek: 1, ecosia: 1, searx: 1, reddit: 1, hackernews: 1, wikipedia: 1, github: 1, archive_org: 1, crt_sh: 1, pastebin: 1, shodan: 1, virustotal: 1, abuseipdb: 1, hibp: 1, emailrep: 1, otx: 1, threatfox: 1, urlhaus: 1, securitytrails: 1 };

// Map module IDs to tab IDs
const MODULE_TO_TAB: Record<string, TabId> = {
  domain_intel: 'domain',
  social: 'social',
  email: 'email',
  network: 'network',
  crypto: 'crypto',
  darkweb: 'darkweb',
  documents: 'timeline',
};

function InvestigationRealResultsInner({ result, investigationType: _investigationType }: Props, ref: React.Ref<InvestigationRealResultsRef>) {
  const findings = result.findings;
  if (!findings) return null;

  const availableTabs: { id: TabId; label: string }[] = [
    { id: 'overview', label: 'Vue d\'ensemble' },
    ...(findings.timeline && findings.timeline.length > 0 ? [{ id: 'timeline' as TabId, label: 'Chronologie' }] : []),
    ...(findings.related_entities && findings.related_entities.length > 0 ? [{ id: 'entities' as TabId, label: 'Entites' }] : []),
    ...(findings.email_breaches && findings.email_breaches.length > 0 ? [{ id: 'email' as TabId, label: 'Email OSINT' }] : []),
    ...(findings.domain_info ? [{ id: 'domain' as TabId, label: 'Domaine' }] : []),
    ...(findings.network_intel ? [{ id: 'network' as TabId, label: 'Reseau' }] : []),
    ...(findings.social_profiles && findings.social_profiles.length > 0 ? [{ id: 'social' as TabId, label: 'Social' }] : []),
    ...(findings.crypto_intel ? [{ id: 'crypto' as TabId, label: 'Crypto' }] : []),
    ...(findings.person_info ? [{ id: 'person' as TabId, label: 'Personne' }] : []),
  ];

  const [activeTab, setActiveTab] = useState<TabId>('overview');

  useImperativeHandle(ref, () => ({
    switchTab: (tabId: TabId) => {
      // Map module ID to tab ID if needed
      const mapped = MODULE_TO_TAB[tabId] || tabId;
      if (availableTabs.some(t => t.id === mapped)) {
        setActiveTab(mapped);
      } else {
        // Fallback to overview
        setActiveTab('overview');
      }
    },
  }));

  const timelineCount = findings.timeline?.length || 0;
  const entitiesCount = findings.related_entities?.length || 0;
  const socialCount = findings.social_profiles?.length || 0;

  return (
    <div className="space-y-6">
      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <SummaryCard label="Statut" value={result.status === 'completed' ? 'Termine' : result.status} color={result.status === 'completed' ? 'text-green-400' : 'text-amber-400'} />
        <SummaryCard label="Sources" value={String(result.sources_count || Object.keys(ALL_SEARCH_ENGINES_COUNT).length)} color="text-cyber-cyan" />
        <SummaryCard label="Evenements" value={String(timelineCount)} color="text-amber-400" />
        <SummaryCard label="Entites" value={String(entitiesCount)} color="text-gray-200" />
        <SummaryCard label="Profils sociaux" value={String(socialCount)} color="text-purple-400" />
      </div>

      {/* Tabs */}
      <div className="border-b border-border-subtle">
        <nav className="flex gap-1 overflow-x-auto pb-px">
          {availableTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap rounded-t-lg transition-colors ${
                activeTab === tab.id
                  ? 'bg-surface-tertiary text-cyber-cyan border border-border-subtle border-b-surface-secondary -mb-px'
                  : 'text-gray-500 hover:text-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab content */}
      {activeTab === 'overview' && <OverviewTab findings={findings} result={result} />}
      {activeTab === 'timeline' && <TimelineTab timeline={findings.timeline || []} />}
      {activeTab === 'entities' && <EntitiesTab entities={findings.related_entities || []} />}
      {activeTab === 'email' && <EmailTab breaches={findings.email_breaches || []} target={result.target} />}
      {activeTab === 'domain' && findings.domain_info && <DomainTab info={findings.domain_info} />}
      {activeTab === 'network' && findings.network_intel && <NetworkTab info={findings.network_intel} />}
      {activeTab === 'social' && <SocialTab profiles={findings.social_profiles || []} />}
      {activeTab === 'crypto' && findings.crypto_intel && <CryptoTab info={findings.crypto_intel} />}
      {activeTab === 'person' && findings.person_info && <PersonTab info={findings.person_info} />}

      {/* Export */}
      <div className="flex justify-end gap-3 pt-4 border-t border-border-subtle">
        <button
          onClick={() => {
            const blob = new Blob([JSON.stringify(result, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `investigation-${result.target}-${new Date().toISOString().slice(0, 10)}.json`;
            a.click();
            URL.revokeObjectURL(url);
          }}
          className="px-4 py-2 rounded-lg border border-border-subtle text-gray-400 hover:text-gray-200 hover:bg-surface-tertiary text-sm transition-colors"
        >
          Exporter JSON
        </button>
      </div>
    </div>
  );
}

const InvestigationRealResults = forwardRef(InvestigationRealResultsInner);
export default InvestigationRealResults;
export { MODULE_TO_TAB };

function SummaryCard({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <div className="bg-surface-tertiary/50 border border-border-subtle rounded-lg p-3 text-center">
      <div className={`text-xl font-bold ${color}`}>{value}</div>
      <div className="text-xs text-gray-500 mt-1">{label}</div>
    </div>
  );
}

function OverviewTab({ findings, result }: { findings: NonNullable<InvestigationResult['findings']>; result: InvestigationResult }) {
  return (
    <div className="space-y-4">
      <div className="glass-panel p-4 space-y-2">
        <h4 className="text-sm font-semibold text-gray-300">Investigation</h4>
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div><span className="text-gray-500">Cible :</span> <span className="text-cyber-cyan font-mono">{result.target}</span></div>
          <div><span className="text-gray-500">Type :</span> <span className="text-gray-300">{result.type}</span></div>
          <div><span className="text-gray-500">Statut :</span> <span className={result.status === 'completed' ? 'text-green-400' : 'text-amber-400'}>{result.status}</span></div>
          <div><span className="text-gray-500">Date :</span> <span className="text-gray-300">{result.created_at ? new Date(result.created_at).toLocaleString('fr-FR') : '-'}</span></div>
        </div>
      </div>

      {/* Quick view of first few timeline events */}
      {findings.timeline && findings.timeline.length > 0 && (
        <div className="glass-panel p-4 space-y-2">
          <h4 className="text-sm font-semibold text-gray-300">Derniers evenements ({findings.timeline.length})</h4>
          <div className="space-y-1 max-h-60 overflow-y-auto">
            {findings.timeline.slice(0, 10).map((event, i) => (
              <div key={i} className="flex items-start gap-2 text-xs py-1 border-b border-border-subtle/50 last:border-0">
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                  event.severity === 'high' || event.severity === 'critical' ? 'bg-red-500/10 text-red-400'
                  : event.severity === 'medium' ? 'bg-amber-500/10 text-amber-400'
                  : 'bg-gray-500/10 text-gray-400'
                }`}>
                  {event.type}
                </span>
                <span className="text-gray-300 flex-1">{event.description}</span>
                <span className="text-gray-600 shrink-0">{event.source}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick view of entities */}
      {findings.related_entities && findings.related_entities.length > 0 && (
        <div className="glass-panel p-4 space-y-2">
          <h4 className="text-sm font-semibold text-gray-300">Entites liees ({findings.related_entities.length})</h4>
          <div className="flex flex-wrap gap-2">
            {findings.related_entities.slice(0, 15).map((entity, i) => (
              <span key={i} className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-surface-tertiary border border-border-subtle text-xs">
                <span className="text-gray-500">{entity.type}</span>
                <span className="text-gray-200">{entity.name}</span>
                <span className="text-gray-600">({Math.round(entity.confidence * 100)}%)</span>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Helper: generate a search URL for a source name
function getSourceUrl(source: string, query?: string): string | null {
  const s = source.toLowerCase();
  const q = encodeURIComponent(query || '');
  if (s.includes('shodan')) return `https://www.shodan.io/search?query=${q}`;
  if (s.includes('censys')) return `https://search.censys.io/search?resource=hosts&q=${q}`;
  if (s.includes('virustotal') || s.includes('vt')) return `https://www.virustotal.com/gui/search/${q}`;
  if (s.includes('abuseipdb')) return `https://www.abuseipdb.com/check/${q}`;
  if (s.includes('hibp') || s.includes('haveibeenpwned')) return `https://haveibeenpwned.com/account/${q}`;
  if (s.includes('whois')) return `https://whois.domaintools.com/${q}`;
  if (s.includes('dns') || s.includes('dnsdumpster')) return `https://dnsdumpster.com/`;
  if (s.includes('google')) return `https://www.google.com/search?q=${q}`;
  if (s.includes('github')) return `https://github.com/search?q=${q}`;
  if (s.includes('twitter') || s.includes('x.com')) return `https://x.com/search?q=${q}`;
  if (s.includes('facebook')) return `https://www.facebook.com/search/top?q=${q}`;
  if (s.includes('linkedin')) return `https://www.linkedin.com/search/results/all/?keywords=${q}`;
  if (s.includes('telegram')) return `https://t.me/s/${q}`;
  if (s.includes('reddit')) return `https://www.reddit.com/search/?q=${q}`;
  if (s.includes('crt.sh') || s.includes('certificate')) return `https://crt.sh/?q=${q}`;
  if (s.includes('blockchain') || s.includes('etherscan')) return `https://etherscan.io/search?f=0&q=${q}`;
  if (s.includes('btc') || s.includes('bitcoin')) return `https://www.blockchain.com/explorer/search?search=${q}`;
  return null;
}

function TimelineTab({ timeline }: { timeline: Array<{ date: string; type: string; description: string; source: string; severity?: string }> }) {
  return (
    <div className="space-y-2 max-h-[500px] overflow-y-auto">
      <div className="pl-4 border-l-2 border-border-subtle space-y-3">
        {timeline.map((event, i) => (
          <div key={i} className="relative">
            <div className={`absolute -left-[21px] top-1 w-2.5 h-2.5 rounded-full border-2 border-surface-primary ${
              event.severity === 'high' || event.severity === 'critical' ? 'bg-red-400'
              : event.severity === 'medium' ? 'bg-amber-400'
              : 'bg-cyber-cyan'
            }`} />
            <div className="flex items-start gap-2">
              <div className="flex-1">
                <div className="text-sm text-gray-300">{event.description}</div>
                <div className="flex items-center gap-2 mt-0.5 text-xs text-gray-500">
                  <span className="px-1.5 py-0.5 rounded bg-surface-tertiary text-gray-400">{event.type}</span>
                  <span>{event.source}</span>
                  {event.date && <span>{new Date(event.date).toLocaleString('fr-FR')}</span>}
                </div>
              </div>
              {(() => {
                const url = getSourceUrl(event.source, event.description.split(' ').slice(0, 3).join(' '));
                return url ? (
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="shrink-0 px-3 py-1 text-xs bg-cyber-cyan/10 text-cyber-cyan rounded-lg hover:bg-cyber-cyan/20 transition-colors"
                  >
                    Voir
                  </a>
                ) : null;
              })()}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function getEntitySearchUrl(name: string, type: string): string | null {
  const q = encodeURIComponent(name);
  if (type === 'domain') return `https://whois.domaintools.com/${name}`;
  if (type === 'ip_address') return `https://www.shodan.io/host/${name}`;
  if (type === 'person') return `https://www.google.com/search?q="${q}"`;
  if (type === 'organization') return `https://www.google.com/search?q="${q}"+site:linkedin.com`;
  if (type === 'social_profile') return `https://www.google.com/search?q=${q}`;
  if (type === 'email') return `https://haveibeenpwned.com/account/${q}`;
  return null;
}

function EntitiesTab({ entities }: { entities: Array<{ name: string; type: string; relationship: string; confidence: number }> }) {
  return (
    <div className="space-y-2">
      {entities.map((entity, i) => (
        <div key={i} className="flex items-center justify-between px-4 py-3 bg-surface-tertiary/50 rounded-lg border border-border-subtle">
          <div className="flex items-center gap-3">
            <span className={`text-xs font-medium px-2 py-0.5 rounded ${
              entity.type === 'person' ? 'bg-blue-500/20 text-blue-400'
              : entity.type === 'domain' ? 'bg-green-500/20 text-green-400'
              : entity.type === 'ip_address' ? 'bg-amber-500/20 text-amber-400'
              : entity.type === 'organization' ? 'bg-purple-500/20 text-purple-400'
              : entity.type === 'social_profile' ? 'bg-pink-500/20 text-pink-400'
              : 'bg-gray-500/20 text-gray-400'
            }`}>
              {entity.type}
            </span>
            <span className="text-sm text-gray-200">{entity.name}</span>
          </div>
          <div className="flex items-center gap-3 text-xs">
            <span className="text-gray-500">{entity.relationship}</span>
            <span className={`font-mono ${entity.confidence >= 0.8 ? 'text-green-400' : entity.confidence >= 0.5 ? 'text-amber-400' : 'text-gray-500'}`}>
              {Math.round(entity.confidence * 100)}%
            </span>
            {(() => {
              const url = getEntitySearchUrl(entity.name, entity.type);
              return url ? (
                <a href={url} target="_blank" rel="noopener noreferrer" className="px-3 py-1 bg-cyber-cyan/10 text-cyber-cyan rounded-lg hover:bg-cyber-cyan/20 transition-colors">Voir</a>
              ) : null;
            })()}
          </div>
        </div>
      ))}
    </div>
  );
}

function EmailTab({ breaches, target }: { breaches: Array<{ breach_name: string; breach_date: string; data_types: string[]; description: string; severity: string; source: string }>; target: string }) {
  return (
    <div className="space-y-3">
      {/* Source links */}
      <div className="glass-panel p-3 flex items-center gap-2 flex-wrap">
        <span className="text-xs text-gray-500">Sources :</span>
        <a href={`https://haveibeenpwned.com/account/${encodeURIComponent(target)}`} target="_blank" rel="noopener noreferrer" className="px-2 py-1 text-xs bg-red-500/10 text-red-400 rounded-lg hover:bg-red-500/20 transition-colors">HaveIBeenPwned</a>
        <a href={`https://dehashed.com/search?query=${encodeURIComponent(target)}`} target="_blank" rel="noopener noreferrer" className="px-2 py-1 text-xs bg-amber-500/10 text-amber-400 rounded-lg hover:bg-amber-500/20 transition-colors">DeHashed</a>
        <a href={`https://intelx.io/?s=${encodeURIComponent(target)}`} target="_blank" rel="noopener noreferrer" className="px-2 py-1 text-xs bg-purple-500/10 text-purple-400 rounded-lg hover:bg-purple-500/20 transition-colors">IntelX</a>
      </div>

      {breaches.length === 0 ? (
        <p className="text-sm text-gray-500 text-center py-8">Aucune fuite trouvee</p>
      ) : (
        breaches.map((breach, i) => (
          <div key={i} className="glass-panel p-4 flex items-center gap-4">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-xs font-bold ${
              breach.severity === 'critical' ? 'bg-red-500/20 text-red-400'
              : breach.severity === 'high' ? 'bg-orange-500/20 text-orange-400'
              : breach.severity === 'medium' ? 'bg-amber-500/20 text-amber-400'
              : 'bg-gray-500/20 text-gray-400'
            }`}>
              {breach.severity === 'critical' ? '!!' : breach.severity === 'high' ? '!' : '~'}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-200">{breach.breach_name}</span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                  breach.severity === 'critical' ? 'bg-red-500/10 text-red-400'
                  : breach.severity === 'high' ? 'bg-orange-500/10 text-orange-400'
                  : breach.severity === 'medium' ? 'bg-amber-500/10 text-amber-400'
                  : 'bg-gray-500/10 text-gray-400'
                }`}>{breach.severity}</span>
              </div>
              <div className="text-xs text-gray-500 mt-0.5">{breach.description}</div>
              <div className="flex items-center gap-2 mt-1 text-[10px] text-gray-600">
                <span>{new Date(breach.breach_date).toLocaleDateString('fr-FR')}</span>
                <span>·</span>
                <span>{breach.data_types.join(', ')}</span>
              </div>
            </div>
            {(() => {
              const url = getSourceUrl(breach.source, breach.breach_name);
              return url ? (
                <a href={url} target="_blank" rel="noopener noreferrer" className="px-3 py-1.5 text-xs bg-cyber-cyan/10 text-cyber-cyan rounded-lg hover:bg-cyber-cyan/20 transition-colors">Voir</a>
              ) : (
                <a href={`https://haveibeenpwned.com/PwnedWebsites#${encodeURIComponent(breach.breach_name)}`} target="_blank" rel="noopener noreferrer" className="px-3 py-1.5 text-xs bg-cyber-cyan/10 text-cyber-cyan rounded-lg hover:bg-cyber-cyan/20 transition-colors">Voir</a>
              );
            })()}
          </div>
        ))
      )}
    </div>
  );
}

function DomainTab({ info }: { info: NonNullable<InvestigationResult['findings']>['domain_info'] }) {
  if (!info) return null;
  return (
    <div className="space-y-4">
      <div className="glass-panel p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold text-gray-300">Domain: {info.domain}</h4>
          <div className="flex gap-2">
            <a href={`https://whois.domaintools.com/${info.domain}`} target="_blank" rel="noopener noreferrer" className="px-3 py-1 text-xs bg-cyber-cyan/10 text-cyber-cyan rounded-lg hover:bg-cyber-cyan/20 transition-colors">WHOIS</a>
            <a href={`https://www.virustotal.com/gui/domain/${info.domain}`} target="_blank" rel="noopener noreferrer" className="px-3 py-1 text-xs bg-green-500/10 text-green-400 rounded-lg hover:bg-green-500/20 transition-colors">VirusTotal</a>
            <a href={`https://crt.sh/?q=${info.domain}`} target="_blank" rel="noopener noreferrer" className="px-3 py-1 text-xs bg-amber-500/10 text-amber-400 rounded-lg hover:bg-amber-500/20 transition-colors">crt.sh</a>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div><span className="text-gray-500">Registrar:</span> <span className="text-gray-300">{info.registrar || '-'}</span></div>
          <div><span className="text-gray-500">Creation:</span> <span className="text-gray-300">{info.creation_date || '-'}</span></div>
          <div><span className="text-gray-500">Expiration:</span> <span className="text-gray-300">{info.expiration_date || '-'}</span></div>
        </div>
      </div>

      {info.dns_records && info.dns_records.length > 0 && (
        <div className="glass-panel p-4 space-y-2">
          <h4 className="text-sm font-semibold text-gray-300">DNS Records ({info.dns_records.length})</h4>
          <div className="space-y-1">
            {info.dns_records.map((rec, i) => (
              <div key={i} className="flex gap-3 text-xs">
                <span className="text-gray-500 w-10 font-mono">{rec.type}</span>
                <span className="text-gray-300 font-mono">{rec.value}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {info.nameservers && info.nameservers.length > 0 && (
        <div className="glass-panel p-4 space-y-2">
          <h4 className="text-sm font-semibold text-gray-300">Nameservers</h4>
          <div className="flex flex-wrap gap-2">
            {info.nameservers.map((ns) => (
              <span key={ns} className="px-2 py-1 text-xs bg-surface-tertiary text-gray-300 rounded font-mono">{ns}</span>
            ))}
          </div>
        </div>
      )}

      {info.ssl_info && (
        <div className="glass-panel p-4 space-y-2">
          <h4 className="text-sm font-semibold text-gray-300">SSL Certificate</h4>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div><span className="text-gray-500">Issuer:</span> <span className="text-gray-300">{info.ssl_info.issuer}</span></div>
            <div><span className="text-gray-500">Subject:</span> <span className="text-gray-300">{info.ssl_info.subject}</span></div>
            <div><span className="text-gray-500">Valid from:</span> <span className="text-gray-300">{info.ssl_info.valid_from}</span></div>
            <div><span className="text-gray-500">Valid to:</span> <span className="text-gray-300">{info.ssl_info.valid_to}</span></div>
          </div>
        </div>
      )}

      {info.technologies && info.technologies.length > 0 && (
        <div className="glass-panel p-4 space-y-2">
          <h4 className="text-sm font-semibold text-gray-300">Technologies</h4>
          <div className="flex flex-wrap gap-2">
            {info.technologies.map((tech) => (
              <span key={tech} className="px-2 py-1 text-xs bg-cyber-cyan/10 text-cyber-cyan rounded">{tech}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function NetworkTab({ info }: { info: NonNullable<InvestigationResult['findings']>['network_intel'] }) {
  if (!info) return null;
  return (
    <div className="space-y-4">
      <div className="glass-panel p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold text-gray-300">Network Intel: {info.ip}</h4>
          <div className="flex gap-2">
            <a href={`https://www.shodan.io/host/${info.ip}`} target="_blank" rel="noopener noreferrer" className="px-3 py-1 text-xs bg-cyber-cyan/10 text-cyber-cyan rounded-lg hover:bg-cyber-cyan/20 transition-colors">Shodan</a>
            <a href={`https://www.abuseipdb.com/check/${info.ip}`} target="_blank" rel="noopener noreferrer" className="px-3 py-1 text-xs bg-red-500/10 text-red-400 rounded-lg hover:bg-red-500/20 transition-colors">AbuseIPDB</a>
            <a href={`https://www.virustotal.com/gui/ip-address/${info.ip}`} target="_blank" rel="noopener noreferrer" className="px-3 py-1 text-xs bg-green-500/10 text-green-400 rounded-lg hover:bg-green-500/20 transition-colors">VirusTotal</a>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div><span className="text-gray-500">ASN:</span> <span className="text-gray-300">{info.asn || '-'}</span></div>
          <div><span className="text-gray-500">ISP:</span> <span className="text-gray-300">{info.isp || '-'}</span></div>
          <div><span className="text-gray-500">Country:</span> <span className="text-gray-300">{info.country || '-'}</span></div>
          <div><span className="text-gray-500">City:</span> <span className="text-gray-300">{info.city || '-'}</span></div>
          {info.reputation_score != null && (
            <div><span className="text-gray-500">Reputation:</span> <span className={`font-mono ${info.reputation_score > 70 ? 'text-green-400' : info.reputation_score > 40 ? 'text-amber-400' : 'text-red-400'}`}>{info.reputation_score}</span></div>
          )}
          <div><span className="text-gray-500">Blacklisted:</span> <span className={info.blacklisted ? 'text-red-400' : 'text-green-400'}>{info.blacklisted ? 'Yes' : 'No'}</span></div>
        </div>
      </div>

      {info.open_ports && info.open_ports.length > 0 && (
        <div className="glass-panel p-4 space-y-2">
          <h4 className="text-sm font-semibold text-gray-300">Open Ports ({info.open_ports.length})</h4>
          <div className="flex flex-wrap gap-2">
            {info.open_ports.map((p, i) => (
              <span key={i} className="px-2 py-1 text-xs bg-amber-500/10 text-amber-400 rounded font-mono">
                {p.port} ({p.service}{p.version ? ` ${p.version}` : ''})
              </span>
            ))}
          </div>
        </div>
      )}

      {info.related_domains && info.related_domains.length > 0 && (
        <div className="glass-panel p-4 space-y-2">
          <h4 className="text-sm font-semibold text-gray-300">Related Domains</h4>
          <div className="flex flex-wrap gap-2">
            {info.related_domains.map((d) => (
              <span key={d} className="px-2 py-1 text-xs bg-surface-tertiary text-gray-300 rounded font-mono">{d}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function SocialTab({ profiles }: { profiles: Array<{ platform: string; username: string; url: string; bio?: string; followers?: number; verified?: boolean }> }) {
  return (
    <div className="space-y-3">
      {profiles.length === 0 ? (
        <p className="text-sm text-gray-500 text-center py-8">Aucun profil social trouve</p>
      ) : (
        profiles.map((profile, i) => (
          <div key={i} className="glass-panel p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-full bg-surface-tertiary flex items-center justify-center text-xs font-bold text-gray-400">
              {profile.platform.slice(0, 2).toUpperCase()}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-200">{profile.platform}</span>
                {profile.verified && <span className="text-xs text-cyber-cyan">Verified</span>}
              </div>
              <div className="text-xs text-gray-400 font-mono">@{profile.username}</div>
              {profile.bio && <div className="text-xs text-gray-500 mt-1">{profile.bio}</div>}
            </div>
            {profile.followers != null && (
              <div className="text-xs text-gray-400">{profile.followers.toLocaleString()} followers</div>
            )}
            <a
              href={profile.url}
              target="_blank"
              rel="noopener noreferrer"
              className="px-3 py-1.5 text-xs bg-cyber-cyan/10 text-cyber-cyan rounded-lg hover:bg-cyber-cyan/20 transition-colors"
            >
              Voir
            </a>
          </div>
        ))
      )}
    </div>
  );
}

function CryptoTab({ info }: { info: NonNullable<InvestigationResult['findings']>['crypto_intel'] }) {
  if (!info) return null;
  const explorerUrl = info.chain === 'ethereum' || info.chain === 'ETH'
    ? `https://etherscan.io/address/${info.address}`
    : info.chain === 'bitcoin' || info.chain === 'BTC'
    ? `https://www.blockchain.com/explorer/addresses/btc/${info.address}`
    : `https://www.blockchain.com/explorer/search?search=${info.address}`;
  return (
    <div className="space-y-4">
      <div className="glass-panel p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold text-gray-300">Crypto Address</h4>
          <div className="flex gap-2">
            <a href={explorerUrl} target="_blank" rel="noopener noreferrer" className="px-3 py-1 text-xs bg-cyber-cyan/10 text-cyber-cyan rounded-lg hover:bg-cyber-cyan/20 transition-colors">Explorer</a>
            <a href={`https://www.chainalysis.com/`} target="_blank" rel="noopener noreferrer" className="px-3 py-1 text-xs bg-amber-500/10 text-amber-400 rounded-lg hover:bg-amber-500/20 transition-colors">Chainalysis</a>
          </div>
        </div>
        <div className="text-xs font-mono text-cyber-cyan break-all">{info.address}</div>
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div><span className="text-gray-500">Chain:</span> <span className="text-gray-300">{info.chain}</span></div>
          <div><span className="text-gray-500">Balance:</span> <span className="text-gray-300">{info.balance || '-'}</span></div>
          <div><span className="text-gray-500">Transactions:</span> <span className="text-gray-300">{info.transaction_count ?? '-'}</span></div>
          {info.risk_score != null && (
            <div><span className="text-gray-500">Risk:</span> <span className={`font-mono ${info.risk_score > 70 ? 'text-red-400' : info.risk_score > 40 ? 'text-amber-400' : 'text-green-400'}`}>{info.risk_score}</span></div>
          )}
        </div>
        {info.tags && info.tags.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {info.tags.map((tag) => (
              <span key={tag} className="px-2 py-0.5 text-xs bg-surface-tertiary text-gray-400 rounded-full">{tag}</span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function PersonTab({ info }: { info: NonNullable<InvestigationResult['findings']>['person_info'] }) {
  if (!info) return null;
  return (
    <div className="space-y-4">
      <div className="glass-panel p-4 space-y-3">
        <h4 className="text-sm font-semibold text-gray-300">Person: {info.name || '-'}</h4>
        <div className="space-y-2 text-xs">
          {info.possible_emails && info.possible_emails.length > 0 && (
            <div>
              <span className="text-gray-500">Emails possibles:</span>
              <div className="flex flex-wrap gap-1 mt-1">
                {info.possible_emails.map((e) => (
                  <span key={e} className="px-2 py-0.5 bg-surface-tertiary text-gray-300 rounded font-mono">{e}</span>
                ))}
              </div>
            </div>
          )}
          {info.possible_phones && info.possible_phones.length > 0 && (
            <div>
              <span className="text-gray-500">Telephones:</span>
              <div className="flex flex-wrap gap-1 mt-1">
                {info.possible_phones.map((p) => (
                  <span key={p} className="px-2 py-0.5 bg-surface-tertiary text-gray-300 rounded font-mono">{p}</span>
                ))}
              </div>
            </div>
          )}
          {info.locations && info.locations.length > 0 && (
            <div>
              <span className="text-gray-500">Localisations:</span>
              <div className="flex flex-wrap gap-1 mt-1">
                {info.locations.map((l) => (
                  <span key={l} className="px-2 py-0.5 bg-surface-tertiary text-gray-300 rounded">{l}</span>
                ))}
              </div>
            </div>
          )}
          {info.employers && info.employers.length > 0 && (
            <div>
              <span className="text-gray-500">Employeurs:</span>
              <div className="flex flex-wrap gap-1 mt-1">
                {info.employers.map((e) => (
                  <span key={e} className="px-2 py-0.5 bg-surface-tertiary text-gray-300 rounded">{e}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {info.social_profiles && info.social_profiles.length > 0 && (
        <SocialTab profiles={info.social_profiles} />
      )}
    </div>
  );
}
